import { readChatStream } from './stream';
import { ProviderError, type ChatRequest, type ChatResult, type ChatTurn } from '../types';

interface AnthropicTextBlock {
  type: 'text';
  text: string;
}

interface AnthropicResponse {
  id: string;
  type: 'message';
  role: 'assistant';
  model: string;
  content: AnthropicTextBlock[];
  stop_reason: string;
  usage: { input_tokens: number; output_tokens: number };
}

/**
 * Anthropic Messages API. In Phase 3.4 we proxy via Vite (`/api/anthropic/...`)
 * so the browser can call it without CORS preflight pain. Phase 5 routes
 * through the real Node backend.
 *
 * @param request — chat turns + opts (model + temperature)
 * @param config  — runtime config (apiKey, baseUrl)
 */
export async function chatAnthropic(
  request: ChatRequest,
  config: { apiKey: string; baseUrl: string },
): Promise<ChatResult> {
  const { messages, opts } = request;
  const { system, conversation } = splitSystem(messages, opts.systemPrompt);

  const body = {
    ...(opts.onDelta ? { stream: true } : {}),
    model: opts.model ?? 'claude-sonnet-4-5-20250929',
    max_tokens: opts.maxTokens ?? 1024,
    temperature: opts.temperature,
    // Spread-or-omit rather than `key: value`: an explicit `undefined` is fine
    // for Anthropic but a stored 0 must survive, and `?? default` would eat it.
    ...(opts.topP === undefined ? {} : { top_p: opts.topP }),
    ...(opts.stopSequences?.length ? { stop_sequences: opts.stopSequences } : {}),
    system,
    messages: conversation.map((m) => ({ role: m.role, content: m.content })),
  };

  const res = await fetch(`${config.baseUrl}/v1/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': config.apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify(body),
    signal: opts.signal,
    redirect: 'error', // don't follow 3xx to an unintended host
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ProviderError('anthropic', res.status, text || res.statusText, text);
  }

  if (opts.onDelta && res.headers.get('content-type')?.includes('text/event-stream')) return readChatStream(res, 'anthropic', opts.onDelta);

  const data = (await res.json()) as AnthropicResponse;
  const text = data.content
    .filter((b): b is AnthropicTextBlock => b.type === 'text')
    .map((b) => b.text)
    .join('\n');
  return {
    text,
    raw: data,
    ...(data.usage
      ? { usage: { inputTokens: data.usage.input_tokens, outputTokens: data.usage.output_tokens } }
      : {}),
  };
}

function splitSystem(messages: ChatTurn[], extraSystem?: string): {
  system: string | undefined;
  conversation: ChatTurn[];
} {
  const systems: string[] = [];
  const conversation: ChatTurn[] = [];
  for (const m of messages) {
    if (m.role === 'system') systems.push(m.content);
    else conversation.push(m);
  }
  if (extraSystem) systems.unshift(extraSystem);
  return { system: systems.length ? systems.join('\n\n') : undefined, conversation };
}
